import { round2 } from "../../global/money";

const sumBy = (items, key) =>
  round2(items.reduce((sum, item) => sum + (parseFloat(item?.[key]) || 0), 0));

// SN008 and SN027 are retail-price based scenarios: sales tax is charged on
// the printed retail price rather than on the sale value.
//
// fixedNotifiedValueOrRetailPrice is stored and shown PER UNIT - it is filled
// from the item master's retailPrice - so the tax on the line is that price
// times the quantity sold. Five units of a 100-rupee 3rd Schedule good at 18%
// carry 5 x 100 x 18% = 90 of sales tax, not 18.
//
// FBR itself reads the field as a whole-line retail value and checks
// `tax == retailPrice x rate`, so the backend multiplies the quantity into the
// price when it builds the FBR payload (see buildFbrItems in
// services/fbrPayload.js). Both sides therefore agree on 90 while the stored
// price stays per-unit and re-posting an edited invoice cannot double-scale.
//
// rateValue reaches here as "18%" (the submit handlers suffix it), which
// parseFloat reads as 18.
export const retailPriceSalesTax = (item) =>
  round2(
    round2(item?.fixedNotifiedValueOrRetailPrice) *
      (Number(item?.quantity) || 0) *
      ((parseFloat(item?.rateValue) || 0) / 100),
  );

// SN008 / SN027 tax the line on its Fixed Notified Value / Retail Price, so a
// line there cannot go without one. The Add and Edit Invoice screens show this
// on the line as soon as its item is picked (or the value is changed) - the
// item master allows 0, which most items carry - and refuse the save with it.
export const RETAIL_PRICE_ZERO_ERROR = "This value could not be zero.";

const isRetailPriceType = (transactionType) =>
  transactionType === "SN008" || transactionType === "SN027";

/** The line's retail-price error under `transactionType`, or "" for none. */
export const retailPriceError = (transactionType, line) =>
  isRetailPriceType(transactionType) &&
  !(Number(line?.fixedNotifiedValueOrRetailPrice) > 0)
    ? RETAIL_PRICE_ZERO_ERROR
    : "";

/**
 * The item errors once the invoice's scenario becomes `transactionType`: every
 * line with an item picked is checked against it - flagged under SN008 /
 * SN027, cleared under any other. A line with no item yet is left alone.
 */
export const withRetailPriceErrors = (transactionType, items, itemErrors) => {
  const next = { ...itemErrors };
  items.forEach((line, index) => {
    if (!line?.itemId) return;
    next[index] = {
      ...next[index],
      fixedNotifiedValueOrRetailPrice: retailPriceError(transactionType, line),
    };
  });
  return next;
};

/**
 * The item errors, which are kept by row number, after row `index` is removed:
 * the rows below it move up with their own errors.
 */
export const itemErrorsWithoutRow = (itemErrors, index) => {
  if (!itemErrors) return itemErrors;
  const next = {};
  Object.entries(itemErrors).forEach(([row, rowErrors]) => {
    const at = Number(row);
    if (at < index) next[at] = rowErrors;
    if (at > index) next[at - 1] = rowErrors;
  });
  return next;
};

// `includeFurtherTax` is set for a company that charges Further Tax
// (global/furtherTax.js): its line totals carry each line's Further Tax, so a
// total rebuilt here has to as well. Left unset, nothing here changes.
export const payloadAccToType = (payload, { includeFurtherTax = false } = {}) => {
  if (
    payload?.transactionType === "SN008" ||
    payload?.transactionType === "SN027"
  ) {
    // The row total and the invoice totals are rebuilt from the recomputed
    // sales tax, otherwise the edit screen - which does not run the
    // retail-price maths while the user types - would submit a tax figure
    // that no longer adds up to the totals sent alongside it. Discount stays
    // a deduction from the amount payable, never from the taxable value.
    const updatedItems = payload.items.map((item) => {
      const salesTaxApplicable = retailPriceSalesTax(item);
      return {
        ...item,
        salesTaxApplicable,
        totalValues: round2(
          (parseFloat(item?.valueSalesExcludingST) || 0) +
            salesTaxApplicable -
            (parseFloat(item?.discount) || 0) +
            (includeFurtherTax ? parseFloat(item?.furtherTax) || 0 : 0),
        ),
      };
    });
    return {
      ...payload,
      items: updatedItems,
      subTotal: sumBy(updatedItems, "valueSalesExcludingST"),
      taxTotal: sumBy(updatedItems, "salesTaxApplicable"),
      grandTotal: sumBy(updatedItems, "totalValues"),
    };
  } else if (payload?.transactionType === "SN023") {
    const updatedItems = payload.items.map((item) => ({
      ...item,
      sroScheduleNo: "581(1)/2024",
      sroItemSerialNo: "Region-I",
    }));
    return {
      ...payload,
      items: updatedItems,
    };
  } else return payload;
};
